import { cn } from '../lib/cn.js';
import { thresholdTone } from '../lib/tones.js';

export interface ProgressSegment {
  /** Proportion 0..1 (segments are normalised). */
  value: number;
  tone: 'ok' | 'info' | 'warn' | 'brand' | 'dim';
  label: string;
}

const SEGMENT_BG: Record<ProgressSegment['tone'], string> = {
  ok: 'bg-ok',
  info: 'bg-info',
  warn: 'bg-warn',
  brand: 'bg-brand',
  dim: 'bg-dim',
};

export type ProgressBarProps = {
  /** Accessible name. */
  label: string;
  className?: string;
} & (
  | {
      /** Single fill 0..1. */
      value: number;
      /** Colour by the 4-tier threshold (default) or force brand. */
      threshold?: boolean;
      segments?: never;
    }
  | { segments: ProgressSegment[]; value?: never; threshold?: never }
);

/** 6px track (--card), square corners; single or segmented fill (DESIGN_SYSTEM section 8.10 + 18.3). */
export function ProgressBar(props: ProgressBarProps) {
  const { label, className } = props;
  if (props.segments) {
    const total = props.segments.reduce((s, x) => s + Math.max(0, x.value), 0) || 1;
    return (
      <div role="img" aria-label={`${label}: ${props.segments.map((s) => `${s.label} ${Math.round((s.value / total) * 100)} percent`).join(', ')}`} className={cn('flex h-1.5 w-full overflow-hidden bg-card', className)}>
        {props.segments.map((s, i) => (
          <div key={`${s.label}-${i}`} title={s.label} className={cn('h-full', SEGMENT_BG[s.tone])} style={{ width: `${(Math.max(0, s.value) / total) * 100}%` }} />
        ))}
      </div>
    );
  }
  const v = Math.min(1, Math.max(0, props.value));
  const tone = props.threshold === false ? 'brand' : thresholdTone(v);
  return (
    <div role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(v * 100)} className={cn('h-1.5 w-full bg-card', className)}>
      <div className={cn('h-full', SEGMENT_BG[tone])} style={{ width: `${v * 100}%` }} />
    </div>
  );
}
